import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";

const PalDraft = lazy(() => import("@/components/games/PalDraft").then((m) => ({ default: m.PalDraft })));
const AchievementWall = lazy(() => import("@/components/games/AchievementWall").then((m) => ({ default: m.AchievementWall })));

const TITLE = "Palworld Random Team Generator & Draft Grader";
const DESCRIPTION =
  "Roll a random five-Pal Palworld team under Nuzlocke-style rules — one Pal per element, one Legendary max — and get it graded for combat, mount and base work.";
const OG_IMAGE = "https://palstars.com/og/palworld-games.jpg";

const STEPS = [
  { name: "Roll the draft", text: "Press roll to draw five Pals at random from the full Pal Stars roster under the draft rules." },
  { name: "Check the rules", text: "A legal draft has one Pal per element and at most one Legendary, which forces genuine variety instead of five late-game flyers." },
  { name: "Read the grade", text: "The team is scored from database ratings: combat, mount quality, base work and element coverage combine into a D-to-S grade." },
  { name: "Run the team", text: "Take the team into a Palworld run as a self-imposed challenge, or share it and dare someone to beat your grade." },
];

const FAQ: FaqItem[] = [
  { q: "What is the Palworld random team generator?", a: "Pal Draft Roulette rolls five random Pals under Nuzlocke-style rules — one Pal per element and a maximum of one Legendary — then grades the team on combat, mount speed, base work and element coverage." },
  { q: "How is the team grade calculated?", a: "The grade weights average PvE rating four times, mount rating and base rating 2.5 times each, and adds 1.5 points per distinct element covered. 78 points or more is an S team, 68 an A, 58 a B and 48 a C." },
  { q: "Can I use a drafted team for a Palworld Nuzlocke?", a: "Yes, that is the point: the roll gives you a legal, element-diverse squad to use as a self-imposed challenge run, and the share link lets a friend attempt the same five Pals." },
  { q: "Does the draft give rewards?", a: "No. Draft rolls are cosmetic, capped at 30 per browser per UTC day, and unlock badges only — packs and spins come from the reward games." },
  { q: "Can I re-roll a bad Palworld team?", a: "Yes, re-roll as often as the daily cap allows. Your best graded score stays saved in the browser so you can chase an S-grade squad." },
];

export const Route = createFileRoute("/pal-draft")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Pal Draft Roulette — random Palworld team generator" },
      { property: "og:description", content: "Roll a legal five-Pal Palworld squad, get it graded D to S and share the team card." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/pal-draft" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/pal-draft" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Games", "/palworld-games"], ["Pal Draft Roulette", "/pal-draft"]]),
      toolSchema({ name: "Palworld Pal Draft Roulette", description: DESCRIPTION, path: "/pal-draft" }),
      howToSchema({ name: "How to roll a random Palworld team", description: "Roll five Pals under draft rules, read the grade and share the squad.", path: "/pal-draft", steps: STEPS }),
      faqSchema(FAQ, "https://palstars.com/pal-draft"),
    ],
  }),
  component: PalDraftPage,
});

function PalDraftPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars Arcade · challenge runs</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld Pal Draft Roulette</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          The Palworld random team generator rolls five legal Pals — one per element, one Legendary at most — and grades
          the squad from D to S using combat, mount and base-work ratings from the Pal Stars database.
        </p>
      </header>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Roll a random Palworld team</h2><p className="mt-2 text-sm text-muted-foreground">Loading the draft…</p></section>}>
        <PalDraft />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Draft rules and grading</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} id={`step-${i + 1}`} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Achievement badges</h2><p className="mt-2 text-sm text-muted-foreground">Loading badges…</p></section>}>
        <AchievementWall game="pal-draft" heading="Draft badges" />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Play next</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/palworld-games" className="font-semibold text-primary hover:underline">All free Palworld games →</Link></li>
          <li><Link to="/team-builder" className="font-semibold text-primary hover:underline">Palworld team builder →</Link> plan a squad on purpose.</li>
          <li><Link to="/tier-list" className="font-semibold text-primary hover:underline">Palworld tier list →</Link></li>
          <li><Link to="/pal-bracket" className="font-semibold text-primary hover:underline">Best Palworld Pal bracket →</Link></li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld team draft FAQ" />
    </div>
  );
}
